import { useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Check, Copy, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const BRL = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2 });

// Renders the PIX / expired / confirmed stages and polls the booking status.
export const PixPayment = ({ stage, booking, rows, confirmedText, onStage, onClose, onRetry }) => {
  useEffect(() => {
    if (stage !== "pix" || !booking?.booking_id) return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/public/bookings/${booking.booking_id}`);
        if (r.data.status === "confirmada") { onStage("confirmed"); toast.success("Pagamento confirmado!"); clearInterval(t); }
        else if (r.data.status === "expirada") { onStage("expired"); clearInterval(t); }
      } catch (e) {
        if (e.response?.status === 404) { onStage("expired"); clearInterval(t); }
      }
    }, 4000);
    return () => clearInterval(t);
  }, [stage, booking, onStage]);

  const copy = () => {
    navigator.clipboard.writeText(booking.pix_payment_code);
    toast.success("Código PIX copiado!");
  };

  if (stage === "expired") return (
    <div className="text-center py-6" data-testid="booking-expired">
      <p className="text-lg font-extrabold text-slate-800 mb-2">PIX expirado</p>
      <p className="text-sm text-slate-500 mb-5">O prazo de 30 minutos para pagamento terminou e a solicitação foi cancelada. As datas podem ser reservadas novamente.</p>
      <button onClick={onRetry} className="bg-[#0A66C2] text-white px-6 py-3 rounded-2xl font-bold" data-testid="booking-retry">Fazer nova reserva</button>
    </div>
  );

  if (stage === "confirmed") return (
    <div className="text-center py-6" data-testid="booking-confirmed">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><Check className="w-10 h-10 text-green-600" /></div>
      <h3 className="text-2xl font-extrabold text-slate-900 mb-2">Pagamento confirmado!</h3>
      <p className="text-slate-500 text-sm mb-6">{confirmedText}</p>
      <div className="bg-green-50 border border-green-100 rounded-2xl p-4 text-sm text-left mb-6 space-y-1">
        {rows.map(([k, v]) => <div key={k}><strong>{k}:</strong> {v}</div>)}
      </div>
      <button onClick={onClose} className="w-full bg-[#0A66C2] text-white py-4 rounded-2xl font-extrabold hover:bg-blue-700 transition-colors" data-testid="close-confirmed">Concluir</button>
    </div>
  );

  return (
    <div className="text-center" data-testid="pix-stage">
      <div className="bg-slate-50 rounded-3xl p-6 mb-4 border border-slate-100">
        {booking.pix_payment_code_base64 ? (
          <img src={`data:image/png;base64,${booking.pix_payment_code_base64}`} alt="QR Code PIX" className="w-56 h-56 mx-auto rounded-xl" data-testid="pix-qr-image" />
        ) : booking.pix_payment_code ? (
          <div className="bg-white p-4 rounded-xl inline-block"><QRCodeSVG value={booking.pix_payment_code} size={220} /></div>
        ) : (
          <div className="text-slate-500 text-sm py-8">O QR Code não pôde ser exibido. Use o código copia e cola abaixo ou fale com o atendimento.</div>
        )}
      </div>
      <p className="text-slate-500 text-sm mb-4">Abra o app do seu banco, escolha PIX e escaneie o QR Code ou use o código copia e cola. O código vale por 30 minutos.</p>
      {booking.pix_payment_code && (
        <button onClick={copy} className="w-full bg-[#0A66C2] text-white py-3 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors mb-3" data-testid="copy-pix-code">
          <Copy className="w-4 h-4" /> Copiar código PIX
        </button>
      )}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 flex items-center gap-2 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" /> Aguardando pagamento. Esta tela será atualizada automaticamente.
      </div>
      <div className="mt-4 text-sm text-slate-500 border-t pt-4">Sinal: <strong>{BRL(booking.deposit_amount)}</strong> · Total: {BRL(booking.total_amount)}</div>
    </div>
  );
};
